// FILE: frontend/src/context/AuthContext.tsx
// SECURE VERSION with SecurityUtils integration

import React, { 
  createContext, 
  useContext, 
  useState, 
  useEffect, 
  ReactNode, 
  useCallback, 
  useMemo 
} from 'react';
import { Admin } from '../types';
import api from '../services/api';
import SecurityUtils from '../utils/SecurityUtils';

// Define the shape of the context's value
interface AuthContextType {
  admin: Admin | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (fullName: string, email: string, password: string, confirmPassword: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
  refreshToken: () => void;
  isSessionValid: () => boolean;
}

// Create the context with an initial undefined value
const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Effect to hydrate auth state from localStorage on initial load
  useEffect(() => {
    try {
      // Get secure token using SecurityUtils
      const storedToken = SecurityUtils.getSecureToken('admin');
      
      if (storedToken) {
        // Validate token format
        if (!SecurityUtils.isValidTokenFormat(storedToken)) {
          console.warn('Invalid token format detected');
          SecurityUtils.clearAllTokens();
          setLoading(false);
          return;
        }

        // Get user data securely
        const userData = SecurityUtils.getSecureUserData('admin');
        
        if (userData) {
          setToken(storedToken);
          setAdmin(userData);
          
          // Log successful session restoration
          SecurityUtils.logSecurityEvent('Session restored', {
            email: userData.email,
            role: 'admin'
          });
        } else {
          // No valid user data found
          SecurityUtils.clearAllTokens();
        }
      }
    } catch (error) {
      console.error("Failed to restore auth session", error);
      SecurityUtils.clearAllTokens();
    } finally {
      setLoading(false);
    }
  }, []);

  // Setup automatic token refresh and tamper detection
  useEffect(() => {
    if (!token) return;

    // Setup auto-refresh for token activity
    const cleanupRefresh = SecurityUtils.setupAutoRefresh('admin', 60000); // Every minute

    // Setup tamper detection
    const cleanupTamper = SecurityUtils.setupTamperDetection(() => {
      console.error('Token tampering detected!');
      logout();
      window.location.href = '/';
    });

    // Check for token expiry warning
    const expiryCheckInterval = setInterval(() => {
      if (SecurityUtils.isTokenExpiringSoon('admin')) {
        console.warn('⚠️ Token expiring soon! Consider implementing token refresh.');
        // You can show a notification to the user here
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => {
      cleanupRefresh();
      cleanupTamper();
      clearInterval(expiryCheckInterval);
    };
  }, [token]);

  // --- Authentication Functions ---

  const login = useCallback(async (email: string, password: string) => {
    try {
      // Sanitize input
      const sanitizedEmail = SecurityUtils.sanitizeInput(email);

      // Validate email
      if (!SecurityUtils.isValidEmail(sanitizedEmail)) {
        throw new Error('Invalid email format');
      }

      // Check for XSS attempts
      if (SecurityUtils.containsXSS(sanitizedEmail)) {
        SecurityUtils.logSecurityEvent('XSS attempt detected in login', { email });
        throw new Error('Invalid input detected');
      }

      // Check rate limiting
      if (!SecurityUtils.checkRateLimit(sanitizedEmail)) {
        const remainingTime = SecurityUtils.getRemainingLockoutTime(sanitizedEmail);
        throw new Error(`Too many failed attempts. Try again in ${remainingTime} minutes.`);
      }

      // Make login API call
      const data = await api.login(sanitizedEmail, password);

      // Validate token format
      if (!SecurityUtils.isValidTokenFormat(data.token)) {
        throw new Error('Invalid token received from server');
      }

      // Clear any previous failed attempts
      SecurityUtils.clearFailedAttempts(sanitizedEmail);

      // Store token and user data securely
      const expiresIn =  24 * 60 * 60 * 1000; // 24 hours default
      SecurityUtils.setSecureToken('admin', data.token, expiresIn);
      SecurityUtils.setSecureUserData('admin', data.admin);

      // Update state
      setToken(data.token);
      setAdmin(data.admin);

      // Log successful login
      SecurityUtils.logSecurityEvent('Login successful', {
        email: sanitizedEmail,
        role: 'admin'
      });

    } catch (error: any) {
      // Record failed attempt
      if (email) {
        SecurityUtils.recordFailedAttempt(SecurityUtils.sanitizeInput(email));
      }

      // Log failed login
      SecurityUtils.logSecurityEvent('Login failed', {
        email: SecurityUtils.sanitizeInput(email),
        error: error.message
      });

      console.error("Login failed:", error);
      throw error;
    }
  }, []);

  const register = useCallback(async (
    fullName: string, 
    email: string, 
    password: string, 
    confirmPassword: string
  ) => {
    try {
      // Sanitize inputs
      const sanitizedEmail = SecurityUtils.sanitizeInput(email);
      const sanitizedFullName = SecurityUtils.sanitizeInput(fullName);

      // Validate email
      if (!SecurityUtils.isValidEmail(sanitizedEmail)) {
        throw new Error('Invalid email format');
      }

      // Check for XSS attempts
      if (SecurityUtils.containsXSS(sanitizedEmail) || SecurityUtils.containsXSS(sanitizedFullName)) {
        SecurityUtils.logSecurityEvent('XSS attempt detected in registration', { 
          email: sanitizedEmail 
        });
        throw new Error('Invalid input detected');
      }

      // Password validation
      if (password !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      if (password.length < 8) {
        throw new Error('Password must be at least 8 characters long');
      }

      // Make registration API call
      await api.register(sanitizedFullName, sanitizedEmail, password, confirmPassword);

      // Log successful registration
      SecurityUtils.logSecurityEvent('Registration successful', {
        email: sanitizedEmail,
        role: 'admin'
      });

    } catch (error: any) {
      // Log failed registration
      SecurityUtils.logSecurityEvent('Registration failed', {
        email: SecurityUtils.sanitizeInput(email),
        error: error.message
      });

      console.error("Registration failed:", error);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      // Log logout event
      SecurityUtils.logSecurityEvent('User logout', {
        email: admin?.email,
        role: 'admin'
      });

      // Clear client-side state
      setToken(null);
      setAdmin(null);

      if (token) {
        try {
          // Attempt to invalidate token on the server
          await api.logout(token);
        } catch (error) {
          console.error('Server logout failed:', error);
        }
      }

      // Clear all secure tokens and session data
      SecurityUtils.clearAllTokens();

    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, clear local data
      SecurityUtils.clearAllTokens();
    }
  }, [token, admin]);

  // Refresh token activity (call this on user interactions)
  const refreshToken = useCallback(() => {
    if (token && admin) {
      SecurityUtils.refreshTokenActivity('admin');
    }
  }, [token, admin]);

  // Check if session is still valid
  const isSessionValid = useCallback((): boolean => {
    if (!token) return false;
    return SecurityUtils.isSessionValid('admin');
  }, [token]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    admin,
    token,
    login,
    register,
    logout,
    isAuthenticated: !!token && isSessionValid(),
    loading,
    refreshToken,
    isSessionValid,
  }), [admin, token, loading, login, register, logout, refreshToken, isSessionValid]);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mb-4"></div>
          <p className="text-gray-600 text-lg">Loading application...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for consuming the AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;